import React, { useState, useEffect, useContext, useCallback } from 'react';
import Link from 'next/link';
import Script from 'next/script';
import { LocationContext } from '../../../context/LocationContext';
import { userActivateEmail } from '../../../api/email/email-api';
import { SystemError } from '../../error/SystemError';
import { LoadingCover } from '../../commons/LoadingCover';
import { registerUser } from '../../../api/user/register-api';
import { setLocalStorageEmail, setLocalStorageJwtToken, setLocalStorageLoginToken } from '../../../js/localstorage';
import { motion, AnimatePresence } from 'framer-motion';
import { googleCaptchaKey } from '../../../utils/constants';
import { COUNTRY_SWITCHER, MARKETS, getRememberedMarket, rememberMarket } from '../../../utils/markets';
import { MarketAddressFields } from '../../commons/MarketAddressFields';
import { User, Mail, Lock, Globe, Eye, EyeOff, Loader2, UserPlus, Briefcase, MapPin, Check, AlertTriangle } from 'lucide-react';

export const SignUpForm = (props) => {
    let formValues = props.formValues;
    let setFormValues = props.setFormValues;
    let goToMoreInfo = props.goToMoreInfo;
    let accountType = props.accountType;
    
    const [ userFullName, setUserFullName ] = useState("");
    const [ isSubmit, setIsSubmit ] = useState(false);
    const [ formErrors, setFormErrors ] = useState({});
    const [ captchaToken, setCaptchaToken ] = useState(""); 
    const [ passwordType, setPasswordType ] = useState("password");
    const [ confirmPasswordType, setConfirmPasswordType ] = useState("password");

    const [ isLoading, setIsLoading ] = useState(false);
    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}); 

    const { countryName, countryCd, countryCurrCd, countryTelCd, location, userLatitude, userLongitude, city, setSgLocationByPostalCode, selectMarketLocation, setLocation } = useContext(LocationContext);

    const isServiceProvider = accountType === "Service Provider";

    // Sign-up is for the served markets only (COUNTRY_SWITCHER: Singapore, UAE,
    // Malaysia). The country is, in order: the visitor's choice here, the
    // country they picked on "/" or last browsed (preferredMarket cookie), the
    // route / browser location, else Singapore. The choice is re-applied if the
    // late browser-location lookup (SignUpLayout getUserLocationDetails) moves
    // the context elsewhere, and a choice made here is remembered site-wide.
    const [ chosenCountryCd, setChosenCountryCd ] = useState(null);
    const isServed = (cd) => COUNTRY_SWITCHER.some((c) => c.countryCd === cd);
    const marketCd = chosenCountryCd ?? (isServed(countryCd) ? countryCd : "sg");
    const market = MARKETS[marketCd];
    const isSingapore = marketCd === "sg";
    const [ address, setAddress ] = useState({ complete: false, userLocation: "" });

    // Read after mount: the page is static, so reading the cookie during
    // render would not match the server HTML.
    useEffect(() => {
        const remembered = getRememberedMarket();
        if (COUNTRY_SWITCHER.some((c) => c.countryCd === remembered)) {
            setChosenCountryCd((prev) => prev ?? remembered);
        }
    }, []);

    useEffect(() => {
        if (countryCd === marketCd) return;
        selectMarketLocation(marketCd, chosenCountryCd ? city : undefined);
    }, [countryCd, marketCd, chosenCountryCd, city, selectMarketLocation]);

    const changeCountry = (e) => {
        if (!isServed(e.target.value)) return;
        setChosenCountryCd(e.target.value);
        rememberMarket(e.target.value);
        setAddress({ complete: false, userLocation: "" });
        setPostalCode("");
        setResolvedAddress("");
        setPostalCodeError("");
        setIsPostalValid(false);
        selectMarketLocation(e.target.value);
        if (formErrors.address || formErrors.postalCode) {
            setFormErrors({...formErrors, address: "", postalCode: ""});
        }
    }

    // UAE / Malaysia address: the composed text is the account location (sign
    // up and the "more info" step both save LocationContext.location); the
    // fields geocode it into userLatitude / userLongitude themselves.
    const onAddressChange = useCallback((next) => {
        setAddress(next);
        if (next.complete) setLocation(next.userLocation);
    }, [setLocation]);

    const [ postalCode, setPostalCode ] = useState("");
    const [ resolvedAddress, setResolvedAddress ] = useState("");
    const [ postalCodeError, setPostalCodeError ] = useState("");
    const [ isValidatingPostal, setIsValidatingPostal ] = useState(false);
    const [ isPostalValid, setIsPostalValid ] = useState(false);

    const handleChange = (e) => {        
        const { name, value } = e.target;
        setFormValues({...formValues, [name]: value});
        // Clear errors on change
        if (formErrors[name]) {
            setFormErrors({...formErrors, [name]: ""});
        }
    }

    const validatePostalCode = async (code) => {
        setIsValidatingPostal(true);
        setPostalCodeError("");
        setResolvedAddress("");
        try {
            const result = await setSgLocationByPostalCode(code);
            setResolvedAddress(result.fullAddress || result.address || "");
            setIsPostalValid(true);
            if (formErrors.postalCode) {
                setFormErrors({...formErrors, postalCode: ""});
            }
        } catch (error) {
            setPostalCodeError(error?.message || "Invalid postal code");
            setIsPostalValid(false);
        } finally {
            setIsValidatingPostal(false);
        }
    }

    const updatePostalCode = (e) => {
        const re = /^[0-9\b]+$/;
        const value = e.target.value;
        if (value === "" || re.test(value)) {
            setPostalCode(value);
            setResolvedAddress("");
            setPostalCodeError("");
            setIsPostalValid(false);
            if (value.length === 6) {
                validatePostalCode(value);
            }
        }
    }
    
    const handleSubmit = (e) => {
        e.preventDefault();
        setFormErrors(validateForm(formValues));
        setUserFullName(formValues.firstName + " " + formValues.lastName);
        grecaptcha.enterprise.ready(async () => {
            const token = await grecaptcha.enterprise.execute(googleCaptchaKey, {action: 'Register'});
            setCaptchaToken(token)
            setIsSubmit(true);
        });
    }

    const validateForm = ( values ) =>{  
        const errors = {};

        if(!values.emailId){
            errors.emailId = "Email Id is required for important communications."
        }
        if(!values.emailId.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)){
            errors.emailId = "Invalid Email Id."
        }
        if(values.password != values.confirmPassword){
            errors.confirmPassword = "Password and confirm password do not match."
        }
        if(values.password.length < 8  || values.password.length > 20){
            errors.password = "Password must be in between 8 to 20 characters."
        }
        if(isSingapore && !isPostalValid){
            errors.postalCode = "Please enter a valid Singapore postal code."
        }
        if(!isSingapore && !address.complete){
            errors.address = market.addressMode === "places-postcode"
                ? "Please enter your street and 5-digit poskod."
                : "Please enter your area and building or villa."
        }
        return errors;
    }

    const createAccount = useCallback(async() => {
        if(!captchaToken) return
        try {            
            gtag('event', 'conversion', {'send_to': 'AW-823304067/3YR1CLWy280DEIO_yogD'});
            fbq('track', 'CompleteRegistration');
        } catch (error) {
            console.log(error)
        }
        setIsLoading(true);
        let registerResponse;
        try {
            registerResponse = await registerUser(
                  formValues.emailId
                , formValues.password
                , formValues.firstName
                , formValues.lastName
                , accountType
                , countryName
                , countryCd
                , countryCurrCd
                , countryTelCd
                , location
                , userLatitude
                , userLongitude
                , captchaToken
            );
        } catch (error){
            console.log(error);
            setErrorContent({'header': "User Registration Error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            setIsSubmit(false);
            return;
        }

        if(registerResponse.responseCode == 0){
            setErrorContent({'header': "System Error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            return;
        }

        if(registerResponse.responseCode == 2){
            setErrorContent({'header': "Email id is taken", 'body': "This email id is already registered! Try with a different email id."});
            setIsSystemError(true);
            setIsLoading(false);
            setIsSubmit(false);
            return;
        }

        if(registerResponse.responseCode == 3){
            setErrorContent({'header': "Security Issues Detected", 'body': "This may happen if you are using a VPN or have security issues with your browser."});
            setIsSystemError(true);
            setIsLoading(false);
            setIsSubmit(false);
            return;
        }

        if(registerResponse.responseCode == 1) {
            setLocalStorageEmail(formValues.emailId);
            setLocalStorageLoginToken(registerResponse.loginToken);
            setLocalStorageJwtToken(registerResponse.jwtToken);

            userActivateEmail(formValues.emailId, userFullName);
            setTimeout(() => {
                setIsLoading(false);
                goToMoreInfo(); 
            }, 1000);
        } else {
            setErrorContent({'header': "System Error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            setIsSubmit(false);
            return;
        }        
    }, [accountType, countryCd, countryCurrCd, countryName, countryTelCd, formValues, goToMoreInfo, location, userFullName, userLatitude, userLongitude, captchaToken])
    
    useEffect(() => {
        if(Object.keys(formErrors).length == 0 && isSubmit){
            createAccount();
        } else {
            setIsSubmit(false);
        }
    }, [formErrors, isSubmit, createAccount])

  return (
    <>
        {/* Header */}
        <div className="px-6 pt-8 pb-4 text-center">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${isServiceProvider ? 'bg-blue-100' : 'bg-orange-100'} flex items-center justify-center`}>
                {isServiceProvider ? (
                    <Briefcase size={28} className="text-blue-500" />
                ) : (
                    <UserPlus size={28} className="text-orange-500" />
                )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
                {isServiceProvider ? 'Service Provider Sign Up' : 'Create Account'}
            </h1>
            <p className="text-gray-500 mt-2 text-sm">
                {isServiceProvider 
                    ? 'Start responding to service requests' 
                    : 'Sign up to request any service'}
            </p>
        </div>

        <form className="px-6 pb-6" onSubmit={handleSubmit}>
            <div className='space-y-4'>
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Given Name
                        </label>
                        <div className="relative">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                <User size={18} className="text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                                placeholder="John"
                                name="firstName"
                                value={formValues.firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Family Name
                        </label>
                        <input
                            type="text"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                            placeholder="Doe"
                            name="lastName"
                            value={formValues.lastName}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>
                
                {/* Email */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                    </label>
                    <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                            <Mail size={18} className="text-gray-400" />
                        </div>
                        <input
                            type="email"
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                            placeholder="john@example.com"
                            name="emailId"
                            value={formValues.emailId}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    {formErrors.emailId && (
                        <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-1 text-sm text-red-500"
                        >
                            {formErrors.emailId}
                        </motion.p>
                    )}
                </div>

                {/* Password */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                    </label>
                    <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                            <Lock size={18} className="text-gray-400" />
                        </div>
                        <input
                            type={passwordType}
                            className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                            placeholder="Enter password"
                            name="password"
                            value={formValues.password}
                            onChange={handleChange}
                            required
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                            onClick={() => setPasswordType(passwordType === "password" ? "text" : "password")}
                        >
                            {passwordType === "password" ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-400">8-20 characters required</p>
                    {formErrors.password && (
                        <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-1 text-sm text-red-500"
                        >
                            {formErrors.password}
                        </motion.p>
                    )}
                </div>

                {/* Confirm Password */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirm Password
                    </label>
                    <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                            <Lock size={18} className="text-gray-400" />
                        </div>
                        <input
                            type={confirmPasswordType}
                            className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                            placeholder="Re-enter password"
                            name="confirmPassword"
                            value={formValues.confirmPassword}
                            onChange={handleChange}
                            required
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                            onClick={() => setConfirmPasswordType(confirmPasswordType === "password" ? "text" : "password")}
                        >
                            {confirmPasswordType === "password" ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                    </div>
                    {formErrors.confirmPassword && (
                        <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-1 text-sm text-red-500"
                        >
                            {formErrors.confirmPassword}
                        </motion.p>
                    )}
                </div>

                {/* Country */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                    </label>
                    <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2">
                            <Globe size={18} className="text-gray-400" />
                        </div>
                        <select 
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all appearance-none"
                            name="countryCd"
                            value={marketCd}
                            onChange={changeCountry}
                        >
                            {COUNTRY_SWITCHER.map((c) =>
                                <option key={c.countryCd} value={c.countryCd}>{c.label}</option>
                            )}
                        </select>
                    </div>
                </div>

                {/* UAE / Malaysia address */}
                {!isSingapore && (
                    <div>
                        {market.cities.length > 1 && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    City
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                        <MapPin size={18} className="text-gray-400" />
                                    </div>
                                    <select
                                        className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all appearance-none"
                                        name="city"
                                        value={city}
                                        onChange={(e) => selectMarketLocation(marketCd, e.target.value)}
                                    >
                                        {market.cities.map((c) =>
                                            <option key={c.slug} value={c.name}>{c.name}</option>
                                        )}
                                    </select>
                                </div>
                            </div>
                        )}
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Address
                        </label>
                        {/* key: fresh fields when the country changes */}
                        <MarketAddressFields key={marketCd} market={market} onChange={onAddressChange} highlight={!!formErrors.address} />
                        {formErrors.address && (
                            <motion.p
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-1 text-sm text-red-500"
                            >
                                {formErrors.address}
                            </motion.p>
                        )}
                    </div>
                )}

                {/* Singapore Postal Code */}
                {isSingapore && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Postal Code
                        </label>
                        <div className="relative">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                <MapPin size={18} className="text-gray-400" />
                            </div>
                            <input
                                type="text"
                                inputMode="numeric"
                                className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
                                placeholder="6-digit postal code"
                                name="postalCode"
                                value={postalCode}
                                onChange={updatePostalCode}
                                minLength={6}
                                maxLength={6}
                                required
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                {isValidatingPostal && <Loader2 size={18} className="text-gray-400 animate-spin" />}
                                {!isValidatingPostal && isPostalValid && <Check size={18} className="text-green-500" />}
                                {!isValidatingPostal && postalCode.length === 6 && !isPostalValid && <AlertTriangle size={18} className="text-yellow-500" />}
                            </div>
                        </div>
                        <AnimatePresence>
                            {isPostalValid && resolvedAddress && (
                                <motion.p
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="mt-1 text-xs text-green-600 truncate"
                                >
                                    {resolvedAddress}
                                </motion.p>
                            )}
                            {!isPostalValid && postalCodeError && postalCode.length === 6 && (
                                <motion.p
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="mt-1 text-sm text-yellow-600"
                                >
                                    {postalCodeError}
                                </motion.p>
                            )}
                            {formErrors.postalCode && (
                                <motion.p
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-1 text-sm text-red-500"
                                >
                                    {formErrors.postalCode}
                                </motion.p>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* Terms Agreement */}
            <div className='mt-6'>
                <label className="flex items-start gap-3 cursor-pointer">
                    <input 
                        type="checkbox" 
                        name="sellerAgreement" 
                        required
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-600">
                        I agree to the{' '}
                        <Link href="/terms" className='text-orange-600 hover:underline' target='_blank' rel='noopener'>Terms</Link>
                        {' '}and{' '}
                        <Link href="/privacy-policy" className='text-orange-600 hover:underline' target='_blank' rel='noopener'>Privacy Policy</Link>
                    </span>
                </label>
            </div>

            {/* Submit Button */}
            <div className='mt-6'>
                <motion.button 
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-3.5 ${isServiceProvider ? 'bg-linear-to-r from-blue-500 to-blue-600' : 'bg-linear-to-r from-orange-500 to-amber-500'} text-white font-semibold rounded-xl shadow-lg ${isServiceProvider ? 'shadow-blue-500/25 hover:shadow-blue-500/30' : 'shadow-orange-500/25 hover:shadow-orange-500/30'} hover:shadow-xl disabled:opacity-50 transition-all flex items-center justify-center gap-2`}
                    type="submit" 
                    disabled={isLoading || (isSingapore ? !isPostalValid : !address.complete)}
                >
                    {isLoading ? <Loader2 size={20} className="animate-spin" /> : null}
                    Sign Up
                </motion.button>
            </div>

            {/* Sign In Link */}
            <div className="mt-4 text-center">
                <p className="text-sm text-gray-500">
                    Already have an account?{' '}
                    <Link href="/signin" className="text-orange-600 font-medium hover:underline">
                        Sign In
                    </Link>
                </p>
            </div>
        </form>
        
        {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
        {isLoading && <LoadingCover/>}
        <Script
            id="google-captcha"
            src={`https://www.google.com/recaptcha/enterprise.js?render=${googleCaptchaKey}`}
            strategy="lazyOnload"
        />
    </>
  )
}
